import { useState, useEffect, useCallback } from 'react';
import {
  BoxSection,
  ButtonDefault,
  ButtonPrimary,
  ConfirmModal,
  EViewBoxColumnType,
  ViewBox,
} from '../../../../../../app/components';
import { TRegisterPersonDataRequest } from '../../../../context';
import { Box, useTheme } from '@mui/material';
import { TableWrapper } from './styles';
import { Heading3, Table, TextL, TextM, TextS, TextXs } from 'app/components';
import { EmptyArea } from 'modules/management/components';
import { usePersonService } from 'modules/customer/services';
import { formatCPF } from 'app/helpers';
import { TScrDetailItem, TScrResponse } from 'modules/customer/interfaces/scr';
import { ArrowBack, RefreshOutlined } from '@mui/icons-material';
import {
  TableBodyCellContainer,
  TableHeadCellContainer,
} from 'app/styles/components/Table';
import { formatCurrency } from 'app/utils/normalizer';
import NaturalPersonViewScrDetailsSideSheet from './NaturalPersonViewScrDetailsSideSheet';
import { useNavigate, useParams } from 'react-router-dom';
import { usePermify } from 'modules/auth/context';
import { EAccountPermissions } from 'modules/auth/context/permify/permify.interfaces';
import { DownloadIcon } from 'app/components/Icons';
import { PDFDownloadLink } from '@react-pdf/renderer';
import NaturalPersonViewScrPDF from './NaturalPersonViewScrPDF';
import {
  NaturalPersonViewPageContentContainer,
  NaturalPersonViewPageContentHeaderButtons,
} from '../NaturalPersonViewPageContent/styles';
import { ScrRequestIcon } from 'app/components/Icons/ScrRequestIcon';

const tableHeader = [
  {
    id: '1',
    content: (
      <TableHeadCellContainer>
        <TextL weight="bold">Modalidade</TextL>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '2',
    content: (
      <TableHeadCellContainer>
        <TextL weight="bold">Valor</TextL>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '3',
    content: (
      <TableHeadCellContainer>
        <TextL weight="bold">Variação cambial</TextL>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '4',
    content: (
      <TableHeadCellContainer>
        <TextL weight="bold">Vencimentos</TextL>
      </TableHeadCellContainer>
    ),
  },
];

const NaturalPersonViewScrDetails = () => {
  const theme = useTheme();
  const [isConfirmRequestScrOpen, toggleConfirmRequestModalOpen] =
    useState(false);
  const [scr, updateScr] = useState<TScrResponse>();
  const [scrDetailItem, updateScrDetailItem] = useState<TScrDetailItem | null>(
    null,
  );
  const [person, setPerson] = useState<TRegisterPersonDataRequest | null>(null);
  const {
    getPerson,
    getPersonScr,
    requestPersonScr,
    requestAcceptanceTermsScr,
    getPersonScrLoading,
    requestPersonScrLoading,
    requestAcceptanceTermsScrLoading,
  } = usePersonService();
  const navigate = useNavigate();
  const { isProfileAuthorized } = usePermify();
  const params = useParams();
  const [isAcceptanceTermsScrOpen, toggleAcceptanceTermsModalOpen] =
    useState(false);

  useEffect(() => {
    if (isProfileAuthorized(EAccountPermissions.READ_SCR)) {
      if (typeof params.id === 'string') {
        getPersonScr(params.id, params.scrId as string).then((scrResponse) => {
          if (scrResponse) updateScr(scrResponse);
        });
        getPerson(params.id).then((b) => {
          setPerson(b);
        });
      }
    } else {
      navigate('/');
    }
  }, [params]);

  const onRequestScr = useCallback(() => {
    if (typeof scr?.dados?.dataBaseConsultada === 'string') {
      requestPersonScr(params.id as string, scr.dados.dataBaseConsultada).then(
        (scrResponse) => {
          if (scrResponse) updateScr(scrResponse);
          toggleConfirmRequestModalOpen(false);
        },
      );
    }
  }, [params, scr]);

  const onRequestAcceptanceTermsScr = useCallback(() => {
    if (!person) return null;
    requestAcceptanceTermsScr(person.id as string).then(() => {
      toggleAcceptanceTermsModalOpen(false);
    });
  }, [person]);

  const hasScrEvidence = true; //person?.has_scr_evidence;

  return (
    <NaturalPersonViewPageContentContainer>
      <ButtonDefault
        style={{
          background: 'transparent',
          border: 'none',
          marginRight: 'auto',
          width: '100px',
          padding: '0px 0px 52px',
          color: theme.palette.brand.primary.base,
        }}
        onClick={() => navigate(-1)}
      >
        <ArrowBack
          style={{
            color: theme.palette.brand.primary.base as string,
            marginRight: '5px',
            fontSize: '22px',
          }}
        />
        <TextM
          weight="Semibold"
          style={{ color: theme.palette.brand.primary.base }}
        >
          Voltar
        </TextM>
      </ButtonDefault>
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {scr?.dados?.dataBaseConsultada && (
            <Heading3 fontWeight="Sb">{`SCR ${scr?.dados?.dataBaseConsultada}`}</Heading3>
          )}
          <NaturalPersonViewPageContentHeaderButtons>
            {scr && isProfileAuthorized(EAccountPermissions.CREATE_SCR) && (
              <ButtonDefault
                style={{
                  maxWidth: '250px',
                  display: 'flex',
                  gap: '8px',
                  justifyContent: 'space-between',
                }}
                onClick={() => {
                  if (hasScrEvidence) {
                    toggleConfirmRequestModalOpen(true);
                    return;
                  }
                  toggleAcceptanceTermsModalOpen(true);
                }}
              >
                <RefreshOutlined
                  style={{
                    color: theme.palette.brand.primary.base as string,
                    fontSize: '22px',
                  }}
                />
                <TextM
                  weight="Semibold"
                  style={{ color: theme.palette.brand.primary.base }}
                >
                  Atualizar consulta
                </TextM>
              </ButtonDefault>
            )}
            {scr && scr.dados && person && (
              <PDFDownloadLink
                style={{
                  textDecoration: 'none',
                }}
                fileName={`SCR_${person.taxpayer_id}.pdf`}
                document={
                  <NaturalPersonViewScrPDF data={scr} borrower={person} />
                }
              >
                {() => {
                  return (
                    <ButtonPrimary
                      typeVariant="outline"
                      style={{
                        gap: '8px',
                        maxWidth: '190px',
                        marginLeft: '10px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        color: theme.palette.brand.primary.base,
                      }}
                    >
                      <DownloadIcon
                        color={theme.palette.brand.primary.base as string}
                      />
                      <TextM
                        weight="Semibold"
                        style={{ color: theme.palette.brand.primary.base }}
                      >
                        Fazer download
                      </TextM>
                    </ButtonPrimary>
                  );
                }}
              </PDFDownloadLink>
            )}
          </NaturalPersonViewPageContentHeaderButtons>
        </div>
        <Box>
          <BoxSection>
            {scr?.dados ? (
              <>
                <ViewBox
                  style={{ marginTop: '0px' }}
                  values={
                    {
                      ...scr.dados,
                    } as unknown as {
                      [key: string]: string | number;
                    }
                  }
                  columns={[
                    {
                      path: 'cnpjDaIFSolicitante',
                      label: 'CNPJ Solicitante',
                      type: EViewBoxColumnType.CNPJ,
                      sufix: '/****-**',
                    },
                    {
                      path: 'codigoDoCliente',
                      label: 'Código do Cliente',
                    },
                    {
                      path: 'tipoDoCliente',
                      label: 'Tipo do Cliente',
                    },
                    {
                      path: 'dataBaseConsultada',
                      label: 'Data base consultada',
                    },
                    {
                      path: 'dataInicioRelacionamento',
                      label: 'Data início relacionamento',
                      type: EViewBoxColumnType.DATE,
                    },
                    {
                      path: 'coobrigacaoAssumida',
                      label: 'Coobrigação assumida',
                      type: EViewBoxColumnType.CURRENCY,
                    },
                    {
                      path: 'coobrigacaoRecebida',
                      label: 'Coobrigação resumida',
                      type: EViewBoxColumnType.CURRENCY,
                    },
                    {
                      path: 'percentualDocumentosProcessados',
                      label: 'Percentual documentos processados',
                      sufix: '%',
                    },
                    {
                      path: 'percentualVolumeProcessado',
                      label: 'Percentual volume processado',
                      sufix: '%',
                    },
                    {
                      path: 'quantidadeDeInstituicoes',
                      label: 'Quantidade de instituições',
                    },
                    {
                      path: 'quantidadeDeOperacoes',
                      label: 'Quantidade de operações',
                    },
                    {
                      path: 'quantidadeOperacoesDiscordancia',
                      label: 'Quantidade de operações discordância',
                    },
                    {
                      path: 'quantidadeOperacoesSubJudice',
                      label: 'Quantidade de operações sub judice',
                    },
                    {
                      path: 'responsabilidadeTotalDiscordancia',
                      label: 'Responsabilidade total discordância',
                    },
                    {
                      path: 'responsabilidadeTotalSubJudice',
                      label: 'Responsabilidade total sub judice',
                    },
                    {
                      path: 'riscoIndiretoVendor',
                      label: 'Risco indireto vendor',
                      type: EViewBoxColumnType.CURRENCY,
                    },
                  ]}
                />
                {scr?.dados?.analise && !Array.isArray(scr.dados.analise) ? (
                  <TableWrapper>
                    <Table
                      tableHeadCell={tableHeader}
                      tableBodyContent={scr.dados.analise.detalhamento.map(
                        (itemDetail, index) => {
                          return {
                            id: `${itemDetail.modalidade.titulo}-${String(
                              index,
                            )}`,
                            cells: [
                              {
                                id: '1',
                                content: (
                                  <TableBodyCellContainer>
                                    <TextS weight="Bold">
                                      {itemDetail.modalidade.titulo ?? '-'}
                                    </TextS>
                                    <TextXs>
                                      {itemDetail.modalidade.subtitulo ?? '-'}
                                    </TextXs>
                                  </TableBodyCellContainer>
                                ),
                              },
                              {
                                id: '2',
                                content: (
                                  <TableBodyCellContainer>
                                    <TextS>
                                      {itemDetail.valor
                                        ? formatCurrency(itemDetail.valor)
                                        : '-'}
                                    </TextS>
                                  </TableBodyCellContainer>
                                ),
                              },
                              {
                                id: '3',
                                content: (
                                  <TableBodyCellContainer>
                                    <TextS>
                                      {itemDetail.variacaoCambial ?? '-'}
                                    </TextS>
                                  </TableBodyCellContainer>
                                ),
                              },
                              {
                                id: '4',
                                content: (
                                  <TableBodyCellContainer>
                                    <ButtonDefault
                                      onClick={() =>
                                        updateScrDetailItem(itemDetail)
                                      }
                                      style={{
                                        border: 'none',
                                        fontWeight: 'normal',
                                        background: 'transparent',
                                        textDecoration: 'underline',
                                        justifyContent: 'flex-start',
                                        padding: '0px',
                                      }}
                                    >
                                      Visualizar
                                    </ButtonDefault>
                                  </TableBodyCellContainer>
                                ),
                              },
                            ],
                          };
                        },
                      )}
                    />
                  </TableWrapper>
                ) : (
                  <div style={{ textAlign: 'center', padding: '48px 0' }}>
                    <TextL>
                      Cliente não possui dados suficientes no SCR para realizar
                      a análise.
                    </TextL>
                  </div>
                )}
              </>
            ) : (
              <EmptyArea
                message="Dados da consulta indisponível"
                createButtonText="Atualizar consulta"
                onCreateClick={
                  isProfileAuthorized(EAccountPermissions.CREATE_SCR)
                    ? () => {
                        if (hasScrEvidence) {
                          toggleConfirmRequestModalOpen(true);
                          return;
                        }
                        toggleAcceptanceTermsModalOpen(true);
                      }
                    : undefined
                }
                isLoading={getPersonScrLoading}
              >
                {null}
              </EmptyArea>
            )}
            <ConfirmModal
              isOpen={isAcceptanceTermsScrOpen}
              title="Termo de aceite não autorizado"
              color="primary"
              width="390px"
              style={{
                padding: '24px 0px',
                minWidth: '470px',
              }}
              btnConfirmCustom={
                <ButtonPrimary
                  typeVariant="fill"
                  style={{
                    width: '120px',
                  }}
                  onClick={() => toggleAcceptanceTermsModalOpen(false)}
                >
                  <TextM
                    weight="Semibold"
                    style={{
                      color: theme.palette.common.white as string,
                    }}
                  >
                    Ok
                  </TextM>
                </ButtonPrimary>
              }
              btnCancelCustom={
                <ButtonPrimary
                  style={{ maxWidth: '265px' }}
                  typeVariant="ghost"
                  iconPosition="right"
                  btnIcon={
                    <ScrRequestIcon
                      color={theme.palette.brand.primary.base as string}
                    />
                  }
                  onClick={onRequestAcceptanceTermsScr}
                >
                  <TextM
                    weight="Semibold"
                    style={{
                      marginRight: '11px',
                      color: theme.palette.brand.primary.base as string,
                    }}
                  >
                    Enviar termo novamente
                  </TextM>
                </ButtonPrimary>
              }
              isLoading={requestAcceptanceTermsScrLoading}
              handleClose={() => toggleAcceptanceTermsModalOpen(false)}
            >
              Para gerar o SCR, é necessário que o Termo de Aceite esteja
              devidamente autorizado.
            </ConfirmModal>
            <ConfirmModal
              isOpen={isConfirmRequestScrOpen}
              title="A realização desta consulta gera custo para o Originador."
              color="primary"
              btnConfirmText="Confirmar"
              isLoading={requestPersonScrLoading}
              handleConfirm={onRequestScr}
              handleClose={() => toggleConfirmRequestModalOpen(false)}
              handleCancel={() => toggleConfirmRequestModalOpen(false)}
            >
              Para consultar o SCR é obrigatório que o titular do CPF{' '}
              <strong>{formatCPF(person?.taxpayer_id || '')}</strong> tenha
              autorizado previamente a consulta, de forma livre e consciente.
              Você confirma que obteve essa autorização?
            </ConfirmModal>
            <NaturalPersonViewScrDetailsSideSheet
              handleClose={() => updateScrDetailItem(null)}
              isOpen={scrDetailItem !== null}
              itemDetail={scrDetailItem}
            />
          </BoxSection>
        </Box>
      </>
    </NaturalPersonViewPageContentContainer>
  );
};

export default NaturalPersonViewScrDetails;
